import { useAppDispatch, useAppSelector } from 'app/hooks';
import Modal from 'components/common/modal';
import Button from 'components/inputs/button';
import SatelliteMapPicker from 'components/structure-page/satellite-map-picker';
import { ProjectAttachmentType } from 'data/types';
import { useAssignImageToStructureItemMutation } from 'features/api/project-api';
import { deleteStructureImage } from 'features/data';
import {
  assignImageToStructureItem,
  setSatelliteView
} from 'features/structure';
import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import axios from 'utils/api';
import { loadStructureAttachments } from 'utils/data';
import { resolvePropertyCoordinates } from 'utils/property';
import { rotateImageFile } from 'utils/rotate-image-file';
import { normalizeHeading } from 'utils/satellite-heading';
import {
  fetchSatelliteImageAsFile,
  SATELLITE_DEFAULT_ZOOM,
  SATELLITE_IMAGE_FILE_NAME,
  SATELLITE_IMAGE_NAME
} from 'utils/satellite-image';
import { appendSatelliteViewToFormData } from 'utils/satellite-view';
import { errorModal } from 'utils/ui';

type EditSatelliteImageModalProps = Readonly<{
  openState: [boolean, Dispatch<SetStateAction<boolean>>];
  attachment: ProjectAttachmentType | null;
}>;

export default function EditSatelliteImageModal({
  openState,
  attachment
}: EditSatelliteImageModalProps) {
  const dispatch = useAppDispatch();
  const [open, setOpen] = openState;
  const {
    structureUniqueId,
    structureId,
    structureAttachments,
    structureImageAssignments,
    satelliteView
  } = useAppSelector((state) => state.structure);
  const { propertyLocation, attomData } = useAppSelector(
    (state) => state.project
  );
  const { googleApiLoaded } = useAppSelector((state) => state.ui);
  const [assignImageToStructureItemMutation] =
    useAssignImageToStructureItemMutation();
  const [isSaving, setIsSaving] = useState(false);
  const [heading, setHeading] = useState(() =>
    normalizeHeading(satelliteView?.heading ?? 0)
  );
  const mapRef = useRef<google.maps.Map | null>(null);

  const propertyCoordinates = useMemo(
    () => resolvePropertyCoordinates(propertyLocation, attomData),
    [propertyLocation, attomData]
  );

  const initialCenter: google.maps.LatLngLiteral | null = useMemo(() => {
    if (satelliteView) {
      return { lat: satelliteView.lat, lng: satelliteView.lng };
    }
    if (propertyCoordinates) {
      return { lat: propertyCoordinates.lat, lng: propertyCoordinates.lng };
    }
    return null;
  }, [satelliteView, propertyCoordinates]);

  const initialZoom = satelliteView?.zoom ?? SATELLITE_DEFAULT_ZOOM;
  const headingLabel = `${Math.round(heading)}°`;

  useEffect(() => {
    if (open) {
      setHeading(normalizeHeading(satelliteView?.heading ?? 0));
      return;
    }
    mapRef.current = null;
  }, [open, satelliteView?.heading]);

  const handleMapReady = (map: google.maps.Map) => {
    mapRef.current = map;
  };

  const nudgeHeading = (delta: number) => {
    setHeading((current) => normalizeHeading(current + delta));
  };

  const handleSave = async () => {
    const map = mapRef.current;
    const center = map?.getCenter();
    if (!map || !center || !attachment || !structureUniqueId || !structureId) {
      return;
    }

    const lat = center.lat();
    const lng = center.lng();
    const zoom = Math.round(map.getZoom() ?? SATELLITE_DEFAULT_ZOOM);
    const savedHeading = normalizeHeading(heading);

    setIsSaving(true);
    try {
      const existingIds = new Set(structureAttachments.map((a) => a.id));
      const previousAssignment =
        structureImageAssignments[attachment.id] ?? null;

      const northUpFile = await fetchSatelliteImageAsFile(
        lat,
        lng,
        SATELLITE_IMAGE_FILE_NAME,
        zoom
      );
      const satelliteFile = await rotateImageFile(northUpFile, savedHeading);

      const formData = new FormData();
      formData.append('structureUniqueId', structureUniqueId);
      formData.append('images', satelliteFile);
      appendSatelliteViewToFormData(formData, {
        lat,
        lng,
        zoom,
        heading: savedHeading
      });

      await axios.post('structure/structure-images', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const updatedAttachments = await loadStructureAttachments(structureId);

      await dispatch(deleteStructureImage(attachment.id));

      const newSatellite = updatedAttachments?.find(
        (a) => !existingIds.has(a.id) && a.name === SATELLITE_IMAGE_NAME
      );

      if (newSatellite && previousAssignment?.structureItemUniqueId) {
        const { structureItemUniqueId } = previousAssignment;
        const structureItemTypeCode =
          previousAssignment.structureItemTypeCode ?? 'level';
        dispatch(
          assignImageToStructureItem({
            attachmentId: newSatellite.id,
            structureItemUniqueId,
            structureItemTypeCode
          })
        );
        try {
          await assignImageToStructureItemMutation({
            attachmentId: newSatellite.id,
            structureItemUniqueId,
            structureItemTypeCode
          }).unwrap();
        } catch (error) {
          console.error('Failed to reassign satellite image to level:', error);
        }
      }

      dispatch(setSatelliteView({ lat, lng, zoom, heading: savedHeading }));
      setOpen(false);
    } catch (error) {
      console.error('Error updating satellite image:', error);
      errorModal('Failed to update satellite image');
    } finally {
      setIsSaving(false);
    }
  };

  const canSave = googleApiLoaded && !!initialCenter && !isSaving;
  const rotationDisabled = !initialCenter || isSaving;

  return (
    <Modal
      title='Edit Satellite Image'
      openState={openState}
      onSave={handleSave}
      buttonText={isSaving ? 'Saving...' : 'Save'}
      saveDisabled={!canSave}
      closeOnSave={false}
      size='wide'
    >
      <div className='flex flex-col gap-4'>
        <p className='text-sm text-gray-600'>
          Drag the map to reposition, scroll to zoom, and rotate freely to match
          the building orientation. The crosshair marks the center of the saved
          satellite image.
        </p>

        {open && googleApiLoaded && initialCenter ? (
          <SatelliteMapPicker
            center={initialCenter}
            zoom={initialZoom}
            heading={heading}
            onMapReady={handleMapReady}
          />
        ) : (
          <div className='mx-auto flex aspect-square w-full max-w-[640px] items-center justify-center rounded-lg border border-gray-200 bg-gray-100 text-sm text-gray-500'>
            {googleApiLoaded
              ? 'No location is available for this property.'
              : 'Loading map...'}
          </div>
        )}

        <div className='flex flex-col gap-2'>
          <label
            htmlFor='satellite-rotation'
            className='text-sm font-medium text-gray-700'
          >
            {`Rotation (${headingLabel})`}
          </label>
          <div className='flex flex-wrap items-center gap-3'>
            <Button
              color='white'
              text='−1°'
              disabled={rotationDisabled}
              onClick={() => nudgeHeading(-1)}
            />
            <input
              id='satellite-rotation'
              type='range'
              min={0}
              max={359}
              step={1}
              value={Math.round(normalizeHeading(heading)) % 360}
              disabled={rotationDisabled}
              onChange={(event) => setHeading(Number(event.target.value))}
              className='min-w-48 flex-1 accent-blue-600'
            />
            <Button
              color='white'
              text='+1°'
              disabled={rotationDisabled}
              onClick={() => nudgeHeading(1)}
            />
            <Button
              color='white'
              text='Reset'
              disabled={rotationDisabled || heading === 0}
              onClick={() => setHeading(0)}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
}
